import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Search, Save, Eye, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const AdminSEO = () => {
  const [seo, setSeo] = useState({
    title: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/content/seo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeo({
        title: response.data.title || '',
        description: response.data.description || ''
      });
    } catch (error) {
      console.error('Error fetching SEO:', error);
      toast.error('Failed to load SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/content/seo`, {
        id: 'seo',
        ...seo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO settings saved successfully');
    } catch (error) {
      console.error('Error saving SEO:', error);
      toast.error('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const titleLength = seo.title.length;
  const descLength = seo.description.length;
  const titleOptimal = titleLength >= 50 && titleLength <= 60;
  const descOptimal = descLength >= 150 && descLength <= 160;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="admin-seo-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-serif text-gray-900">SEO Settings</h1>
          <p className="text-gray-600 mt-1">Manage your website's search engine optimization</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-accent hover:bg-accent/90"
          data-testid="save-seo-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Eye className="w-4 h-4" />
          <span>Google Search Preview</span>
        </div>
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="text-blue-600 text-xl hover:underline cursor-pointer truncate">
            {seo.title || 'Your Page Title'}
          </div>
          <div className="text-green-700 text-sm mt-1">
            thesingstudio.com
          </div>
          <div className="text-gray-600 text-sm mt-1 line-clamp-2">
            {seo.description || 'Your page description will appear here...'}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
        {/* Title */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              <Search className="w-4 h-4 inline mr-2" />
              Page Title (Browser Tab)
            </label>
            <span className={`text-xs ${titleOptimal ? 'text-green-600' : titleLength > 60 ? 'text-red-600' : 'text-amber-600'}`}>
              {titleLength}/60 characters
            </span>
          </div>
          <input
            type="text"
            value={seo.title}
            onChange={(e) => setSeo({ ...seo, title: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent transition-colors"
            placeholder="Your page title for search engines"
            data-testid="seo-title-input"
          />
          <div className="mt-2 flex items-start gap-2">
            <AlertCircle className={`w-4 h-4 mt-0.5 ${titleOptimal ? 'text-green-500' : 'text-amber-500'}`} />
            <p className="text-xs text-gray-500">
              {titleOptimal 
                ? 'Great! Your title length is optimal for search engines.' 
                : 'Optimal length is 50-60 characters. Longer titles may be truncated in search results.'}
            </p>
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Meta Description
            </label>
            <span className={`text-xs ${descOptimal ? 'text-green-600' : descLength > 160 ? 'text-red-600' : 'text-amber-600'}`}>
              {descLength}/160 characters
            </span>
          </div>
          <textarea
            value={seo.description}
            onChange={(e) => setSeo({ ...seo, description: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent transition-colors resize-none"
            placeholder="A brief description of your page for search results"
            data-testid="seo-description-input"
          />
          <div className="mt-2 flex items-start gap-2">
            <AlertCircle className={`w-4 h-4 mt-0.5 ${descOptimal ? 'text-green-500' : 'text-amber-500'}`} />
            <p className="text-xs text-gray-500">
              {descOptimal 
                ? 'Perfect! Your description length is ideal.' 
                : 'Optimal length is 150-160 characters. This appears below your title in search results.'}
            </p>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">SEO Tips</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Include your main keywords (singing lessons, Yateley, vocal coach)</li>
            <li>• Make the title compelling to increase click-through rates</li>
            <li>• The description should summarize what visitors will find</li>
            <li>• Changes may take a few days to appear in search results</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSEO;
